import { ComponentProps, memo, useCallback, useRef, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import styled from '@mint-lib/styled';
import type {} from '@mint-lib/theme/src/extensions/palette.js';

import {
  Box,
  Button,
  Card,
  Icon,
  IconButton,
  Popover,
  PopoverItem,
  SelectWithSearch,
  Tooltip,
  Typography,
} from '@myn/mui';

import {
  useIntegrationAccountTypeRouteApi,
  useIntegrationPaths,
} from '../../../hooks/useIntegrationsRouteApi.js';
import { useIsAccountCreation } from '../../../hooks/useIsAccountCreation.js';

type IntegrationType = {
  integration: string;
  logo: string | null;
  filter: {
    filterValue: string[];
    setFilterValue: (value: string[]) => void;
    filterOptions?: { label: string; value: string }[];
  };
  sorting: {
    sortValue: {
      type: 'date' | 'alphabet';
      direction: 'asc' | 'desc';
    };
    setSortValue: (value: {
      type: 'date' | 'alphabet';
      direction: 'asc' | 'desc';
    }) => void;
  };
  disableAddAccountButton: boolean;
};

const AddAccountButton = ({
  integration,
  logo,
  filter,
  sorting,
  disableAddAccountButton,
}: IntegrationType) => {
  const { t } = useTranslation('@myn/integrations_v2');
  const isAccountCreation = useIsAccountCreation();
  const navigate = useIntegrationAccountTypeRouteApi().useNavigate();
  const paths = useIntegrationPaths();
  const handleClick = () => {
    navigate({
      to: paths.integrationType,
      params: {
        integration,
        tabName: 'accounts',
        type: 'add',
      },
    });
  };

  const filterButtonRef = useRef(null);
  const sortButtonRef = useRef(null);
  const { direction, type } = sorting.sortValue;

  const handleAddAccount = useCallback(() => {
    if (isAccountCreation) {
      return;
    }

    navigate({
      to: paths.integrationType,
      params: {
        integration,
        tabName: 'accounts',
        type: 'add',
      },
    });
  }, [isAccountCreation, navigate, paths.integrationType, integration]);

  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
  const [isSortOpen, setIsSortOpen] = useState<boolean>(false);
  const handleToggleFilter = () => {
    setIsFilterOpen(!isFilterOpen);
  };
  const handleToggleSort = () => {
    setIsSortOpen(!isSortOpen);
  };

  const handleChangeFilterValue = (value: string[]) => {
    filter.setFilterValue(value);
  };

  return (
    <div className="flex flex-col">
      <div className="m-4 mr-4 mb-4 ml-0 flex gap-2">
        <Button
          color="secondary"
          size="large"
          startIcon="Add"
          fullWidth
          disabled={isAccountCreation || disableAddAccountButton}
          onClick={handleAddAccount}
          data-dd-action-name="add account"
        >
          {t('Add account')}
        </Button>
        {filter.filterOptions?.length ? (
          <>
            <Tooltip title={t('Filter')} arrow={false}>
              <IconButton
                size="large"
                icon="Filter"
                variant="outlined"
                ref={filterButtonRef}
                onClick={handleToggleFilter}
                data-testid="filter-button"
                data-dd-action-name="open account filter"
              />
            </Tooltip>
            <Popover
              anchorEl={filterButtonRef.current}
              open={isFilterOpen}
              onClose={() => setIsFilterOpen(false)}
              anchorOrigin={{
                horizontal: 'left',
                vertical: 'bottom',
              }}
            >
              <div className="w-75 px-2 py-3">
                <SelectWithSearch
                  label={t('LAST UPDATED BY')}
                  placeholder={t('Select user')}
                  fullWidth
                  multiple
                  options={filter.filterOptions}
                  value={filter.filterValue}
                  onChange={handleChangeFilterValue}
                  largeDropdown
                  truncatedOptions
                  data-testid="filter-select"
                  getOpenState={(state: boolean) => {
                    // close popover when close select
                    setIsFilterOpen(!state);
                  }}
                />
              </div>
            </Popover>

            <Tooltip title={t('Sort by')} arrow={false}>
              <IconButton
                size="large"
                icon="Sort"
                variant="outlined"
                ref={sortButtonRef}
                onClick={handleToggleSort}
                data-testid="sort-button"
                data-dd-action-name="open account sort"
              />
            </Tooltip>
            <Popover
              anchorEl={sortButtonRef.current}
              open={isSortOpen}
              onClose={() => setIsSortOpen(false)}
              anchorOrigin={{
                horizontal: 'left',
                vertical: 'bottom',
              }}
            >
              <div className="w-37.5 pt-2 pr-2 pb-0 pl-2">
                <Typography variant="overline01">
                  {t('Recently updated')}
                </Typography>
                <PopoverItem
                  startAdornment={<Icon component="TimelineDown" />}
                  selected={direction === 'asc' && type === 'date'}
                  text={t('More recently')}
                  onItemClick={() =>
                    sorting.setSortValue({
                      type: 'date',
                      direction: 'asc',
                    })
                  }
                  sx={{ margin: '0 -8px' }}
                  data-testid="date-asc"
                />
                <PopoverItem
                  startAdornment={<Icon component="TimelineUp" />}
                  selected={direction === 'desc' && type === 'date'}
                  text={t('Less recently')}
                  onItemClick={() =>
                    sorting.setSortValue({
                      type: 'date',
                      direction: 'desc',
                    })
                  }
                  sx={{ margin: '0 -8px' }}
                  data-testid="date-desc"
                />
                <Typography variant="overline01">{t('User name')}</Typography>
                <PopoverItem
                  startAdornment={<Icon component="SortAscending" />}
                  selected={direction === 'asc' && type === 'alphabet'}
                  text={t('A-Z')}
                  onItemClick={() =>
                    sorting.setSortValue({
                      type: 'alphabet',
                      direction: 'asc',
                    })
                  }
                  sx={{ margin: '0 -8px' }}
                  data-testid="alphabet-asc"
                />
                <PopoverItem
                  startAdornment={<Icon component="SortDescending" />}
                  selected={direction === 'desc' && type === 'alphabet'}
                  text={t('Z-A')}
                  onItemClick={() =>
                    sorting.setSortValue({
                      type: 'alphabet',
                      direction: 'desc',
                    })
                  }
                  sx={{ margin: '0 -8px' }}
                  data-testid="alphabet-desc"
                />
              </div>
            </Popover>
          </>
        ) : null}
      </div>
      {isAccountCreation ? (
        <StyledCard data-testid="add" selected={true} onClick={handleClick}>
          <Logo>
            {logo ? (
              <img src={logo} />
            ) : (
              <Icon component="Settings" fontSize="large" />
            )}
          </Logo>
          <Typography variant="h5" mr={3}>
            {t('New Account')}
          </Typography>
        </StyledCard>
      ) : null}
    </div>
  );
};

// TODO: migrate to tailwind - styled with options argument (e.g., shouldForwardProp)
const StyledCard = styled(
  ({
    sx = { mx: 2, mb: 1, ml: 0, p: 1.5, gap: 1 },
    ...props
  }: ComponentProps<typeof Card>) => <Card sx={sx} {...props} />,
)`
  height: 60px;
  display: flex;
  align-items: center;
  cursor: pointer;
`;

// TODO: migrate to tailwind - Contains nested selector or at-rule: (?:^|[;\s}])(?:[a-z][a-z0-9]*)\s*\{
const Logo = styled(Box)`
  img {
    height: 40px;
    width: 40px;
  }
  svg {
    height: 40px;
    width: 40px;
  }
`;

export default memo(AddAccountButton);
